import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import StorageIcon from '@mui/icons-material/Storage';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LinkIcon from '@mui/icons-material/Link';
import axios, { Canceler } from 'axios';
import api from '../../../services/api';

export type OptionType = { value: string; label: string };
export type LinkType = {
  targetTable: string;
  targetDesc: string;
  targetField: string;
};
export type FieldType = {
  fldnam: string;
  title: string;
  desfld: string;
  dattyp: string;
  lenfld: number;
  required: boolean;
  isKey: boolean;
  mask: string;
  link?: LinkType | null;
  tipo: string;
  enunam: string;
  options: OptionType[];
};
export type TableType = {
  tblnam: string;
  destbl: string;
  tipo: string;
  fieldsCount: number;
  fields: FieldType[];
};

const BATCH_SIZE = 40;

function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function HighlightText({ text, query, darkMode }: { text: string; query: string; darkMode: boolean }) {
  const q = query.trim();
  if (!q || !text) return <>{text}</>;

  const parts = text.split(new RegExp(`(${escapeRegExp(q)})`, 'gi'));

  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === q.toLowerCase() ? (
          <Box
            key={index}
            component="mark"
            sx={{
              backgroundColor: darkMode ? '#ffc71d' : '#ffe082',
              color: 'inherit',
              px: '2px',
              borderRadius: '2px',
              fontWeight: 'inherit',
            }}
          >
            {part}
          </Box>
        ) : (
          part
        ),
      )}
    </>
  );
}

export default function SeniorDictionaryPage() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [searchTable, setSearchTable] = useState('');
  const [searchField, setSearchField] = useState('');
  const [system, setSystem] = useState<'erp' | 'hcm'>('erp');
  const [allTables, setAllTables] = useState<TableType[]>([]);
  const [loading, setLoading] = useState(true);

  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const observerTarget = useRef<HTMLDivElement | null>(null);

  const loadSystemData = useCallback(async (currentSystem: 'erp' | 'hcm') => {
    let cancel: Canceler;
    setLoading(true);

    try {
      const response = await api.get<TableType[]>('it/senior-tables', {
        params: { system: currentSystem, refresh: 'true' },
        cancelToken: new axios.CancelToken(c => {
          cancel = c;
        }),
      });

      setAllTables(response.data || []);
      setLoading(false);
    } catch (error) {
      if (!axios.isCancel(error)) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadSystemData(system);
  }, [system, loadSystemData]);

  useEffect(() => {
    setVisibleCount(BATCH_SIZE);
  }, [searchTable, searchField, system]);

  const filteredTables = useMemo(() => {
    const termTable = searchTable.trim().toUpperCase();
    const termField = searchField.trim().toUpperCase();

    const hasTableFilter = termTable.length > 0;
    const hasFieldFilter = termField.length > 0;

    if (!hasTableFilter && !hasFieldFilter) return allTables;

    return allTables.filter(table => {
      let matchTable = true;
      let matchField = true;

      if (hasTableFilter) {
        matchTable = table.tblnam?.toUpperCase().includes(termTable) || table.destbl?.toUpperCase().includes(termTable);
      }

      if (hasFieldFilter) {
        matchField = table.fields?.some(
          field =>
            field.fldnam?.toUpperCase().includes(termField) ||
            field.title?.toUpperCase().includes(termField) ||
            field.desfld?.toUpperCase().includes(termField),
        );
      }

      return matchTable && matchField;
    });
  }, [allTables, searchTable, searchField]);

  useEffect(() => {
    const target = observerTarget.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && visibleCount < filteredTables.length) {
          setVisibleCount(prev => prev + BATCH_SIZE);
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [visibleCount, filteredTables.length]);

  const visibleTables = useMemo(() => {
    return filteredTables.slice(0, visibleCount);
  }, [filteredTables, visibleCount]);

  const handleSystemChange = (_event: React.MouseEvent<HTMLElement>, newSystem: 'erp' | 'hcm' | null) => {
    if (newSystem !== null) {
      setSystem(newSystem);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', width: '100%' }}>
      {/* Cabeçalho */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StorageIcon color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Dicionário de Dados Senior
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Consulte tabelas, campos, chaves e relacionamentos do {system === 'erp' ? 'ERP Sapiens' : 'HCM Vetorh'}
            </Typography>
          </Box>
        </Box>

        <ToggleButtonGroup value={system} exclusive onChange={handleSystemChange} color="primary" size="small">
          <ToggleButton value="erp" sx={{ px: 2, gap: 1 }}>
            <BusinessIcon fontSize="small" />
            ERP Senior (Sapiens)
          </ToggleButton>
          <ToggleButton value="hcm" sx={{ px: 2, gap: 1 }}>
            <PeopleIcon fontSize="small" />
            HCM Senior (Vetorh)
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Buscas */}
      <Paper sx={{ p: 2, mb: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Buscar por Tabela ou Descrição"
              placeholder="Digite para pesquisar (ex: E000ACR)..."
              value={searchTable}
              onChange={e => setSearchTable(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Buscar por Campo ou Descrição"
              placeholder="Digite para pesquisar (ex: CodEmp)..."
              value={searchField}
              onChange={e => setSearchField(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Contador */}
      <Box sx={{ mb: 2, px: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {loading
            ? 'Carregando estrutura...'
            : `Exibindo ${visibleTables.length} de ${filteredTables.length} tabelas (${allTables.length} no total)`}
        </Typography>
      </Box>

      {/* Spinner */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress size={32} />
          <Typography sx={{ ml: 2 }} color="text.secondary">
            Carregando dicionário do {system === 'erp' ? 'ERP' : 'HCM'}...
          </Typography>
        </Box>
      )}

      {/* Sem resultados */}
      {!loading && filteredTables.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Nenhuma tabela ou campo encontrado com os termos pesquisados.</Typography>
        </Paper>
      )}

      {/* Lista de Tabelas */}
      {!loading &&
        visibleTables.map(table => (
          <Accordion
            key={table.tblnam}
            slotProps={{ transition: { unmountOnExit: true } }}
            sx={{ mb: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                <Typography fontWeight="bold" color="primary.main" sx={{ minWidth: 120 }}>
                  <HighlightText text={table.tblnam} query={searchTable} darkMode={isDarkMode} />
                </Typography>
                <Typography sx={{ flexGrow: 1, color: 'text.primary' }}>
                  <HighlightText text={table.destbl} query={searchTable} darkMode={isDarkMode} />
                </Typography>
                <Chip
                  label={table.tipo}
                  size="small"
                  color={table.tipo === 'Personalizada' ? 'secondary' : 'default'}
                  variant="outlined"
                />
                <Chip label={`${table.fieldsCount} campos`} size="small" />
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', pl: 2, gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {table.tblnam}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  -
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {table.destbl}
                </Typography>
              </Box>
              <TableContainer component={Paper} square variant="outlined">
                <Table size="small">
                  <TableHead sx={{ backgroundColor: 'action.hover' }}>
                    <TableRow>
                      <TableCell>
                        <strong>Campo / Relacionamento</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Título / Descrição</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Tipo / Formato</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Tamanho / Edição</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Requisito</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {table.fields.map(field => (
                      <TableRow key={field.fldnam} hover>
                        {/* Campo com Ícone de Chave e Relacionamento Link */}
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                              <span>
                                <HighlightText text={field.fldnam} query={searchField} darkMode={isDarkMode} />
                              </span>
                              {field.isKey && (
                                <Tooltip title="Chave Primária">
                                  <VpnKeyIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                                </Tooltip>
                              )}
                              {field.tipo === 'Personalizado' && (
                                <Chip
                                  label="Usuário"
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: 10 }}
                                />
                              )}
                            </Box>

                            {/* Link de Relacionamento */}
                            {field.link && (
                              <Tooltip
                                title={`Relacionado com ${field.link.targetTable}.${field.link.targetField || field.fldnam}`}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    pl: field.isKey ? 3 : 0,
                                    color: 'text.secondary',
                                    fontWeight: 'normal',
                                    fontSize: 12,
                                  }}
                                >
                                  <LinkIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                                  <span>
                                    <strong>{field.link.targetTable}</strong> - {field.link.targetDesc}
                                  </span>
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>

                        {/* Título e Descrição */}
                        <TableCell>
                          <Typography variant="body2" fontWeight="500">
                            <HighlightText text={field.title} query={searchField} darkMode={isDarkMode} />
                          </Typography>
                          {field.desfld && field.desfld !== field.title && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              <HighlightText text={field.desfld} query={searchField} darkMode={isDarkMode} />
                            </Typography>
                          )}
                        </TableCell>

                        {/* Tipo Convertido + Opções */}
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Chip
                              label={field.dattyp}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 11, height: 20, fontFamily: 'monospace' }}
                            />

                            {field.options && field.options.length > 0 && (
                              <Tooltip
                                title={
                                  <Box sx={{ p: 0.5 }}>
                                    <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
                                      Opções de {field.enunam}:
                                    </Typography>
                                    {field.options.map(opt => (
                                      <Typography key={opt.value} variant="caption" display="block">
                                        <strong>{opt.value}</strong> - {opt.label}
                                      </Typography>
                                    ))}
                                  </Box>
                                }
                                arrow
                              >
                                <Chip
                                  icon={<FormatListBulletedIcon style={{ fontSize: 14 }} />}
                                  label={`Lista: ${field.enunam || `${field.options.length} opções`}`}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: 10, cursor: 'pointer' }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>

                        {/* Tamanho e Máscara */}
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            {field.lenfld > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                Tamanho: {field.lenfld}
                              </Typography>
                            )}
                            {field.mask && (
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                Edição: {field.mask}
                              </Typography>
                            )}
                            {!field.lenfld && !field.mask && (
                              <Typography variant="caption" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </Box>
                        </TableCell>

                        {/* Requisito: Obrigatório vs Opcional */}
                        <TableCell align="center">
                          {field.required ? (
                            <Chip
                              label="Obrigatório"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ height: 20, fontSize: 10, fontWeight: 'bold' }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Opcional
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}

      <div ref={observerTarget} style={{ height: 20, margin: '10px 0' }} />
    </Box>
  );
}
